import { FC, useEffect } from 'react';

import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { InputDatepicker, Stack } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { DeceasedFlowDeceaseDateStepRendering } from '@sitecore/types/DeceasedFlowDeceaseDateStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextDeceased } from '../../context';

interface FormFields {
  dateDeceased: string;
}

export const DeceaseDate: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextDeceased>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const dateDeceased = useFlowSelector(state => state.context.dateDeceased);
  const { DeceasedFlowDeceaseDateStep: { fields: textualData } = {} } = usePlaceholderContent<{
    DeceasedFlowDeceaseDateStep: DeceasedFlowDeceaseDateStepRendering;
  }>();

  const { date } = useFormatter();

  const formSchema = yup.object({
    dateDeceased: yup.string().required(textualData?.deceaseDate?.deceaseDateFormField?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    getValues,
    control,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      dateDeceased,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formField = getValues();
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...formField,
        },
      });
    }
  }, [getValues, isSubmitSuccessful, sendToFlowMachine]);

  return (
    <Layout.Content variant="B" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
      </Header>
      <Stack.Item>
        <Controller
          control={control}
          name="dateDeceased"
          render={({ field: { onChange, value, name } }) => (
            <InputDatepicker
              label={textualData?.deceaseDate?.deceaseDateFormField?.value?.label ?? ''}
              hint={textualData?.deceaseDate?.deceaseDateFormField?.value?.hint ?? ''}
              placeholder={textualData?.deceaseDate?.deceaseDateFormField?.value?.placeholder ?? ''}
              error={errors.dateDeceased?.message}
              toDate={new Date()}
              value={value}
              name={name}
              onDaySelect={d => onChange(d && date.short(d as Date))}
            />
          )}
        />
      </Stack.Item>
      <Stack.Item>
        <NextButton isLoading={isSubmitSuccessful} alignX="start">
          {textualData?.content.nextStepText?.value}
        </NextButton>
      </Stack.Item>
    </Layout.Content>
  );
};
